<template>
  <el-dialog
    title="選擇票匣"
    :visible="true"
    width="600px"
    :close-on-click-modal="false"
    @close="$emit('close')"
  >
    <header class="flex mb-[16px]">
      <BaseElInput v-model="searchName" placeholder="請輸入票匣名稱" clearable>
        <i slot="suffix" class="el-input__icon el-icon-search" />
      </BaseElInput>
    </header>

    <main>
      <div class="ticket-grid text-primary-100" style="padding-top: 21px; padding-bottom: 16px">
        <BaseElCheckbox :disabled="true" @change="() => {}">
          <p class="text-primary-100">票匣名稱</p>
        </BaseElCheckbox>
        <p class="text-center">有效期限</p>
      </div>

      <BaseElCheckboxGroup v-model="selected">
        <div v-for="item in displayData[tableOptions.page - 1]" :key="item.id">
          <hr>
          <div class="ticket-grid" style="padding: 16px 0">
            <div class="flex items-center gap-[4px] min-w-0">
              <BaseElCheckbox :label="item.id" :disabled="disabledItem(item)">
                <span class="inline-flex items-center gap-[4px] max-w-full min-w-0">
                  <span class="truncate">{{ item.name }}</span>
                  <TipInfo v-if="disabledReason(item)" :size="20" placement="top">
                    {{ disabledReason(item) }}
                  </TipInfo>
                </span>
              </BaseElCheckbox>
            </div>
            <p class="text-center text-sub text-gray-80">{{ formatExpiry(item) }}</p>
          </div>
        </div>
      </BaseElCheckboxGroup>

      <EmptyBlock v-if="flattenDeep(displayData).length === 0" />
    </main>

    <div>
      <Pagination
        :curPage.sync="tableOptions.page"
        :pageLimit="tableOptions.pageLimit"
        :total="flattenDeep(displayData).length"
      />
    </div>

    <div class="flex justify-end items-center" style="padding-top: 26px">
      <BaseElButton plain @click="$emit('close')">取消</BaseElButton>
      <BaseElButton type="primary" :disabled="!selected.length" @click="onSubmit">確認</BaseElButton>
    </div>
  </el-dialog>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted } from 'vue'
import { chunk, flattenDeep } from 'lodash'
import { useTable } from '@/use/table'
import { formatDate } from '@/utils/date'
import dayjs from '@/lib/dayjs'
import EmptyBlock from '@/components/EmptyBlock.vue'
import TipInfo from '@/components/TipInfo.vue'

export default defineComponent({
  name: 'TicketBoxAdvanceSelect',
  components: { EmptyBlock, TipInfo },
  props: {
    data: { type: Array, default: () => [] },
    selectedData: { type: Array, default: () => [] },
  },
  emits: ['close', 'confirm'],
  setup (props, { emit }) {
    const { tableOptions } = useTable()
    tableOptions.pageLimit = 8

    const searchName = ref('')
    const selected = ref([])

    const filteredData = computed(() => {
      if (!searchName.value) return props.data
      return props.data.filter(item => item.name.includes(searchName.value))
    })

    const displayData = computed(() => chunk(filteredData.value, tableOptions.pageLimit))

    const isExpired = (row) => {
      if (row.expType === 'expAt' && row.expAt) {
        return dayjs(row.expAt).isBefore(dayjs())
      }
      return false
    }

    const disabledItem = (item) => {
      // 已選中的永遠可以取消
      if (selected.value.includes(item.id)) return false
      // 已過期
      if (isExpired(item)) return true
      // 單選模式：已有選中就 disable 其他
      if (selected.value.length > 0) return true
      return false
    }

    const disabledReason = (item) => {
      if (isExpired(item)) return '已過期'
      return ''
    }

    const findTargetPage = (id) => {
      const index = filteredData.value.findIndex(item => item.id === id)
      if (index === -1) return 1
      return Math.ceil((index + 1) / tableOptions.pageLimit)
    }

    const initializeSelection = () => {
      if (props.selectedData && props.selectedData.length > 0) {
        const firstId = props.selectedData[0]
        const itemExists = props.data.some(item => item.id === firstId)
        if (!itemExists) return
        selected.value = [...props.selectedData]
        tableOptions.page = findTargetPage(firstId)
      }
    }

    // 搜尋時重置頁碼，若已選項目在築選結果內則自動跳頁
    watch(searchName, () => {
      if (selected.value.length > 0) {
        const firstId = selected.value[0]
        const visible = filteredData.value.some(item => item.id === firstId)
        if (visible) {
          tableOptions.page = findTargetPage(firstId)
        } else {
          tableOptions.page = 1
        }
      } else {
        tableOptions.page = 1
      }
    })

    const formatExpiry = (ticketBox) => {
      if (!ticketBox) return '-'
      const { expType, expDay, expAt, endOfPeriodUnit } = ticketBox
      if (expType === 'expAt') return formatDate(expAt, 'YYYY-MM-DD')
      if (expType === 'expDay') return `領取後 ${expDay || '-'} 天`
      if (expType === 'endOfPeriod') {
        const unitMap = { day: '當日', week: '當週最後一天', month: '當月最後一天', quarter: '當季最後一天', year: '當年最後一天' }
        return unitMap[endOfPeriodUnit] || '-'
      }
      return '-'
    }

    const onSubmit = () => {
      emit('confirm', selected.value)
      emit('close')
    }

    onMounted(() => {
      initializeSelection()
    })

    return {
      searchName,
      selected,
      displayData,
      tableOptions,
      flattenDeep,
      disabledItem,
      disabledReason,
      formatExpiry,
      onSubmit,
    }
  },
})
</script>

<style scoped lang="postcss">
.ticket-grid {
  @apply grid gap-[20px] text-normal;
  grid-template-columns: 1fr 160px;
}
::v-deep .el-checkbox {
  @apply w-full;
}
::v-deep .el-checkbox .el-checkbox__label {
  @apply min-w-0 flex-1;
}
</style>
